import { useNavigate } from 'react-router-dom'
import { useI18n } from '../../i18n'
import type { DataMapFlowOperationalSummary } from '../../services/enterpriseOperationalUxApi'
import { getStandardDisplayName } from '../../utils/complianceStandard'
import { formatRelativeTime } from '../../utils/operationalUx'
import DataMapOperationalIndicators from './DataMapOperationalIndicators'
import styles from '../../styles/components/assets/AssetsDataMap.module.scss'

type Props = {
  flow: DataMapFlowOperationalSummary
  selected?: boolean
  onSelect: () => void
}

function workflowLabel(stage: string, t: (k: string, d: string) => string) {
  const map: Record<string, string> = {
    READINESS_ESTABLISHED: t('enterpriseOperationalUx.dataMap.workflowReady', 'Readiness established'),
    EVIDENCE_IN_PROGRESS: t('enterpriseOperationalUx.dataMap.workflowEvidence', 'Evidence in progress'),
    DEGRADED: t('enterpriseOperationalUx.dataMap.workflowDegraded', 'Degraded'),
    AUDIT_AT_RISK: t('enterpriseOperationalUx.dataMap.workflowAuditRisk', 'Audit at risk'),
    VERIFIED: t('enterpriseOperationalUx.dataMap.workflowVerified', 'Verified'),
  }
  return map[stage] || stage
}

export default function DataMapOperationalFlowCard({ flow, selected, onSelect }: Props) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const op = flow.operationalState
  const maxPressure =
    op.sourcePressure === 'CRITICAL' || op.targetPressure === 'CRITICAL'
      ? 'CRITICAL'
      : op.sourcePressure === 'HIGH' || op.targetPressure === 'HIGH'
        ? 'HIGH'
        : op.sourcePressure || op.targetPressure

  return (
    <article
      className={`${styles.topologyFlowCard} ${selected ? styles.topologyFlowCardSelected : ''} ${op.degraded ? styles.topologyFlowDegraded : ''} ${flow.dataContext.criticalData ? styles.topologyFlowCritical : ''}`.trim()}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect()
        }
      }}
    >
      <div className={styles.topologyFlowHeader}>
        <h4 className={styles.topologyFlowTitle}>
          {flow.sourceSystemName} → {flow.targetSystemName}
        </h4>
        <DataMapOperationalIndicators
          compact
          pressure={maxPressure}
          degraded={op.degraded}
          staleEvidence={op.staleEvidence}
          criticalData={flow.dataContext.criticalData}
          runtimeWarning={Boolean(flow.runtime.latestTopologyEvent)}
          drift={flow.runtime.driftDetected}
        />
      </div>

      <p className={styles.topologyFlowMeta}>
        {flow.dataCategory} · {flow.flowType} · {flow.direction}
        {flow.crossBorderTransfer ? ` · ${t('assets.dataMap.crossBorder', 'Cross-border')}` : ''}
        {flow.thirdPartyInvolved ? ` · ${t('enterpriseOperationalUx.dataMap.thirdParty', 'Third party')}` : ''}
      </p>

      {flow.dataContext.complianceIndicators.length ? (
        <p className={styles.topologyFlowStandards}>
          {flow.dataContext.complianceIndicators.map((c) => getStandardDisplayName(t, c)).join(' · ')}
        </p>
      ) : null}

      {flow.storytelling ? <p className={styles.topologyStory}>{flow.storytelling}</p> : null}

      {flow.dataContext.scopeReasoning ? (
        <p className={styles.topologyScopeReason}>{flow.dataContext.scopeReasoning}</p>
      ) : null}

      <div className={styles.topologyFlowMetrics}>
        {op.operationalRiskLevel ? (
          <span>
            {t('enterpriseOperationalUx.dataMap.operationalRisk', 'Risk')}: {op.operationalRiskLevel}
          </span>
        ) : null}
        {flow.blastRadius.blastRadiusContribution > 0 ? (
          <span>
            {t('enterpriseOperationalUx.dataMap.blastRadiusContribution', 'Blast radius')}: {flow.blastRadius.blastRadiusContribution}
          </span>
        ) : null}
        {op.degradedControls > 0 ? (
          <span>
            {t('executiveGovernance.degradedControls', 'Degraded controls')}: {op.degradedControls}
          </span>
        ) : null}
      </div>

      {flow.runtime.latestTopologyEvent ? (
        <p className={styles.topologyRuntimeLine}>
          <span className={styles.runtimeDot} aria-hidden />
          {flow.runtime.latestTopologyEvent.eventType} — {formatRelativeTime(flow.runtime.latestTopologyEvent.at)} —{' '}
          {flow.runtime.latestTopologyEvent.summary}
        </p>
      ) : null}

      {flow.workflowStages.length ? (
        <div className={styles.topologyWorkflowRow}>
          {flow.workflowStages.slice(0, 4).map((w) => (
            <button
              key={w.standardCode}
              type="button"
              className={styles.workflowChip}
              onClick={(e) => {
                e.stopPropagation()
                navigate(`/compliance-workflow/${encodeURIComponent(w.standardCode)}`)
              }}
            >
              {getStandardDisplayName(t, w.standardCode)}: {workflowLabel(w.workflowStage, t)}
            </button>
          ))}
        </div>
      ) : null}
    </article>
  )
}
